/**
 * One `−`/value/`+` counter, shared by DICE.app's ROLLING bar (`look: "bar"`, `dice-app.css`) and
 * COMMS's dice popover (`look: "comms"`, `comms-dice.css`). Same markup and aria-labels in both, so a
 * test or flow drives either one by "Increase Accuracy" / "Accuracy value".
 */
const LOOKS = {
  comms: { row: "comms-dice-row", label: "comms-dice-label", controls: "comms-dice-controls", btn: "comms-dice-step", value: "comms-dice-value" },
  bar: { row: "dice-bar-step", label: "dice-bar-steplabel", controls: "dice-bar-stepcontrols", btn: "dice-bar-stepbtn", value: "dice-bar-stepvalue" },
};

export function DiceStepper({
  label,
  value,
  onChange,
  min,
  tone,
  look,
  short,
}: {
  label: string;
  /** The visible caption when it differs from `label` (which always names the controls), e.g. "ACC". */
  short?: string;
  value: number;
  onChange: (next: number) => void;
  /** Floor the counter can't go below (accuracy/difficulty are non-negative counts). Omitted ⇒ unbounded: bonus is a signed value with no floor or ceiling. */
  min?: number;
  /** Tints the value digit so accuracy/difficulty read as opposing quantities at a glance; omitted for the plain Bonus counter. */
  tone?: "acc" | "diff";
  look: keyof typeof LOOKS;
}) {
  const c = LOOKS[look];
  const atFloor = min !== undefined && value <= min;
  return (
    <div className={c.row}>
      <div className={c.label}>{(short ?? label).toUpperCase()}</div>
      <div className={c.controls}>
        <button
          type="button"
          className={c.btn}
          aria-label={`Decrease ${label}`}
          disabled={atFloor}
          onClick={() => onChange(min !== undefined ? Math.max(min, value - 1) : value - 1)}
        >
          −
        </button>
        <span aria-label={`${label} value`} className={tone ? `${c.value} ${c.value}--${tone}` : c.value}>
          {value}
        </span>
        <button type="button" className={c.btn} aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)}>
          +
        </button>
      </div>
    </div>
  );
}
